import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import {
    SearchLg,
    Eye,
    UserX01,
    Trash01,
    Users01,
    Mail01,
    Package,
    CheckCircle,
    XCircle,
    Clock
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Tabs } from '@/components/application/tabs/tabs';
import { Button } from '@/components/base/buttons/button';
import { collaborationService } from '@/data/mock-collaborations';
import { containerService } from '@/data/mock-containers';
import { accountService } from '@/data/mock-accounts';
import type { Collaboration } from '@/types/collaboration';
import { useTranslation } from 'react-i18next';

interface ShipperWithStats {
    id: string;
    legalName: string;
    email: string;
    country: string;
    collaboration: Collaboration;
    activeContainers: number;
    totalContainers: number;
    lastContainerDate?: Date;
}

export const ReceiverShippersPage = () => {
    const { t } = useTranslation(['pages:shippers', 'common']);
    const [activeShippers, setActiveShippers] = useState<ShipperWithStats[]>([]);
    const [receivedInvites, setReceivedInvites] = useState<ShipperWithStats[]>([]);
    const [sentInvites, setSentInvites] = useState<ShipperWithStats[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [activeTab, setActiveTab] = useState('active');

    const countryOptions = [
        { id: '', label: t('list.allCountries') },
        { id: 'United States', label: 'United States' },
        { id: 'Germany', label: 'Germany' },
        { id: 'Italy', label: 'Italy' },
        { id: 'Spain', label: 'Spain' }
    ];

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = localStorage.getItem('accountId') || '2';

            // Load all collaborations for current receiver
            const collaborations = collaborationService.getByReceiverId(accountId);

            // Load all shipper accounts
            const allAccounts = accountService.getAll();
            const shipperAccounts = allAccounts.filter(acc => acc.userType === 'SHIPPER');

            // Load containers for statistics
            const allContainers = containerService.getAll();
            const receiverContainers = allContainers.filter(c => c.receiverId === accountId);

            // Helper function to create shipper with stats
            const createShipperWithStats = (collab: Collaboration): ShipperWithStats | null => {
                const shipperAccount = shipperAccounts.find(acc => acc.id === collab.shipperId);
                if (!shipperAccount) return null;

                const shipperContainers = receiverContainers.filter(c => c.shipperId === collab.shipperId);
                const activeContainers = shipperContainers.filter(c => c.status === 'In Transit' || c.status === 'At Port').length;
                const totalContainers = shipperContainers.length;
                const lastContainer = shipperContainers.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];

                return {
                    id: shipperAccount.id,
                    legalName: shipperAccount.legalName,
                    email: shipperAccount.email,
                    country: shipperAccount.country,
                    collaboration: collab,
                    activeContainers,
                    totalContainers,
                    lastContainerDate: lastContainer?.createdAt
                };
            };

            // Separate by status
            const activeCollaborations = collaborations.filter(c => c.status === 'Active');
            const receivedPendingCollaborations = collaborations.filter(c => c.status === 'Pending' && c.receiverId === accountId);
            const sentPendingCollaborations = collaborations.filter(c => c.status === 'Pending' && c.shipperId === accountId);

            setActiveShippers(activeCollaborations.map(createShipperWithStats).filter(Boolean) as ShipperWithStats[]);
            setReceivedInvites(receivedPendingCollaborations.map(createShipperWithStats).filter(Boolean) as ShipperWithStats[]);
            setSentInvites(sentPendingCollaborations.map(createShipperWithStats).filter(Boolean) as ShipperWithStats[]);

            setIsLoading(false);
        };
        loadData();
    }, []);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Pending': return 'warning';
            case 'Rejected': return 'error';
            case 'Suspended': return 'error';
            default: return 'gray';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'Active': return t('list.status.active');
            case 'Pending': return t('list.status.pending');
            case 'Rejected': return t('list.status.rejected');
            case 'Suspended': return t('list.status.suspended');
            default: return status;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Active': return CheckCircle;
            case 'Pending': return Clock;
            case 'Rejected': return XCircle;
            case 'Suspended': return UserX01;
            default: return Clock;
        }
    };

    const handleAcceptInvite = (collaborationId: string) => {
        if (window.confirm(t('list.receivedInvites.confirmAccept'))) {
            const success = collaborationService.update(collaborationId, {
                id: collaborationId,
                status: 'Active'
            });
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const handleRejectInvite = (collaborationId: string) => {
        if (window.confirm(t('list.receivedInvites.confirmReject'))) {
            const success = collaborationService.update(collaborationId, {
                id: collaborationId,
                status: 'Rejected'
            });
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const handleSuspendCollaboration = (collaborationId: string) => {
        if (window.confirm(t('list.confirmSuspend'))) {
            const success = collaborationService.update(collaborationId, {
                id: collaborationId,
                status: 'Suspended'
            });
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const handleDeleteCollaboration = (collaborationId: string) => {
        if (window.confirm(t('list.confirmDelete'))) {
            const success = collaborationService.delete(collaborationId);
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const filterShippers = (shippers: ShipperWithStats[]) => {
        return shippers.filter(shipper => {
            const matchesSearch = !searchTerm ||
                shipper.legalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                shipper.email.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesCountry = !countryFilter || shipper.country === countryFilter;

            return matchesSearch && matchesCountry;
        });
    };

    const filteredActiveShippers = filterShippers(activeShippers);
    const filteredReceivedInvites = filterShippers(receivedInvites);
    const filteredSentInvites = filterShippers(sentInvites);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    const tabItems = [
        { id: 'active', label: `${t('list.tabs.active')} (${filteredActiveShippers.length})` },
        { id: 'pending', label: `${t('list.tabs.pending')} (${filteredReceivedInvites.length + filteredSentInvites.length})` }
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('list.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.countryPlaceholder')}
                        items={countryOptions}
                        selectedKey={countryFilter}
                        onSelectionChange={(key) => setCountryFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Tabs */}
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as string)}>
                <Tabs.List items={tabItems}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* Tab 1: Active Shippers */}
                <Tabs.Panel id="active" className="space-y-6">
                    <TableCard.Root>
                        <TableCard.Header
                            title={`${t('list.active.title')} (${filteredActiveShippers.length})`}
                            description={t('list.active.description')}
                        />

                        <Table>
                            <Table.Header>
                                <Table.Head label={t('list.active.headers.company')} isRowHeader />
                                <Table.Head label={t('list.active.headers.country')} />
                                <Table.Head label={t('list.active.headers.status')} />
                                <Table.Head label={t('list.active.headers.activeContainers')} />
                                <Table.Head label={t('list.active.headers.totalContainers')} />
                                <Table.Head label={t('list.active.headers.lastContainer')} />
                                <Table.Head label={t('list.active.headers.collaborationDate')} />
                                <Table.Head label={t('list.active.headers.actions')} />
                            </Table.Header>
                            <Table.Body>
                                {filteredActiveShippers.map((shipper) => {
                                    const StatusIcon = getStatusIcon(shipper.collaboration.status);
                                    return (
                                        <Table.Row key={shipper.id}>
                                            <Table.Cell>
                                                <div className="font-medium text-primary">{shipper.legalName}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Package className="size-4 text-tertiary" />
                                                    <span className="text-sm text-tertiary">{shipper.country}</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <StatusIcon className="size-4" />
                                                    <Badge color={getStatusColor(shipper.collaboration.status)} size="sm">
                                                        {getStatusLabel(shipper.collaboration.status)}
                                                    </Badge>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Package className="size-4 text-tertiary" />
                                                    <span className="text-sm text-primary">{shipper.activeContainers}</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">{shipper.totalContainers}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {shipper.lastContainerDate
                                                        ? shipper.lastContainerDate.toLocaleDateString('it-IT')
                                                        : t('list.none')
                                                    }
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {shipper.collaboration.acceptedAt?.toLocaleDateString('it-IT') ||
                                                        shipper.collaboration.invitedAt.toLocaleDateString('it-IT')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Link
                                                        to={`/receiver/shippers/${shipper.id}`}
                                                        className="text-brand-secondary hover:text-brand-secondary_hover"
                                                    >
                                                        <Eye className="size-4" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleSuspendCollaboration(shipper.collaboration.id)}
                                                        className="text-warning-600 hover:text-warning-700"
                                                    >
                                                        <UserX01 className="size-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteCollaboration(shipper.collaboration.id)}
                                                        className="text-error-600 hover:text-error-700"
                                                    >
                                                        <Trash01 className="size-4" />
                                                    </button>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                </Tabs.Panel>

                {/* Tab 2: Pending Invites */}
                <Tabs.Panel id="pending" className="space-y-6">
                    {/* Received Invites */}
                    {filteredReceivedInvites.length > 0 && (
                        <TableCard.Root>
                            <TableCard.Header
                                title={`${t('list.receivedInvites.title')} (${filteredReceivedInvites.length})`}
                                description={t('list.receivedInvites.description')}
                            />

                            <Table>
                                <Table.Header>
                                    <Table.Head label={t('list.receivedInvites.headers.company')} isRowHeader />
                                    <Table.Head label={t('list.receivedInvites.headers.country')} />
                                    <Table.Head label={t('list.receivedInvites.headers.email')} />
                                    <Table.Head label={t('list.receivedInvites.headers.inviteDate')} />
                                    <Table.Head label={t('list.receivedInvites.headers.actions')} />
                                </Table.Header>
                                <Table.Body>
                                    {filteredReceivedInvites.map((shipper) => (
                                        <Table.Row key={shipper.id}>
                                            <Table.Cell>
                                                <div className="font-medium text-primary">{shipper.legalName}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Package className="size-4 text-tertiary" />
                                                    <span className="text-sm text-tertiary">{shipper.country}</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Mail01 className="size-4 text-tertiary" />
                                                    <span className="text-sm text-tertiary">{shipper.email}</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {shipper.collaboration.invitedAt.toLocaleDateString('it-IT')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        onClick={() => handleAcceptInvite(shipper.collaboration.id)}
                                                        size="sm"
                                                        color="primary"
                                                        className="inline-flex items-center gap-1"
                                                    >
                                                        <CheckCircle className="size-4" />
                                                        {t('list.receivedInvites.accept')}
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleRejectInvite(shipper.collaboration.id)}
                                                        size="sm"
                                                        color="secondary-destructive"
                                                        className="inline-flex items-center gap-1"
                                                    >
                                                        <XCircle className="size-4" />
                                                        {t('list.receivedInvites.reject')}
                                                    </Button>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    )}

                    {/* Sent Invites */}
                    {filteredSentInvites.length > 0 && (
                        <TableCard.Root>
                            <TableCard.Header
                                title={`${t('list.sentInvites.title')} (${filteredSentInvites.length})`}
                                description={t('list.sentInvites.description')}
                            />

                            <Table>
                                <Table.Header>
                                    <Table.Head label={t('list.sentInvites.headers.company')} isRowHeader />
                                    <Table.Head label={t('list.sentInvites.headers.country')} />
                                    <Table.Head label={t('list.sentInvites.headers.email')} />
                                    <Table.Head label={t('list.sentInvites.headers.inviteDate')} />
                                    <Table.Head label={t('list.active.headers.status')} />
                                </Table.Header>
                                <Table.Body>
                                    {filteredSentInvites.map((shipper) => (
                                        <Table.Row key={shipper.id}>
                                            <Table.Cell>
                                                <div className="font-medium text-primary">{shipper.legalName}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Package className="size-4 text-tertiary" />
                                                    <span className="text-sm text-tertiary">{shipper.country}</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Mail01 className="size-4 text-tertiary" />
                                                    <span className="text-sm text-tertiary">{shipper.email}</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {shipper.collaboration.invitedAt.toLocaleDateString('it-IT')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color="warning" size="sm">
                                                    In Attesa
                                                </Badge>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    )}

                    {filteredReceivedInvites.length === 0 && filteredSentInvites.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-tertiary mb-4">
                                <Users01 className="size-12 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-primary mb-2">Nessun invito pending</h3>
                                <p className="text-tertiary">
                                    {searchTerm || countryFilter
                                        ? 'Prova a modificare i filtri di ricerca'
                                        : 'Non ci sono inviti in attesa di accettazione'
                                    }
                                </p>
                            </div>
                        </div>
                    )}
                </Tabs.Panel>
            </Tabs>
        </div>
    );
};
